import { useLiveQuery } from '@electric-sql/pglite-react';
import {
  emailProblem,
  grantableRoles,
  nameProblem,
  phoneProblem,
  ROLE_LABELS,
  type Role,
} from '@quickreport/domain';
import { Badge, Button, Field, Input } from '@quickreport/ui';
import { Monitor, Trash2, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api, describeError } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { DeleteLabPanel } from './DeleteLabPanel';
import { DesktopPanel } from './DesktopPanel';
import { OfflinePinPanel } from './OfflinePinPanel';
import { PasswordPanel } from './PasswordPanel';

interface MemberRow {
  id: string;
  user_id: string;
  display_name: string;
  roles: string[];
}
interface DeviceRow {
  id: string;
  name: string;
  platform: string;
  last_seen_at: string | null;
  revoked_at: string | null;
}
interface InviteRow {
  id: string;
  identifier: string;
  displayName: string;
  roles: string[];
}

export function TeamPage() {
  const ws = useWorkspace();
  const canInvite = ws.can('invite_users');
  const members = useLiveQuery<MemberRow>(
    'select id, user_id, display_name, roles from members where org_id = $1 and deleted_at is null order by display_name',
    [ws.orgId],
  );
  const devices = useLiveQuery<DeviceRow>(
    'select id, name, platform, last_seen_at, revoked_at from devices where org_id = $1 and deleted_at is null order by created_at',
    [ws.orgId],
  );
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [name, setName] = useState(ws.auth.userName);

  const loadInvites = useCallback(async () => {
    if (!canInvite) return;
    try {
      setInvites((await api<{ items: InviteRow[] }>('GET', '/v1/invites')).items);
    } catch {
      // offline: the list stays as it was
    }
  }, [canInvite]);
  useEffect(() => {
    void loadInvites();
  }, [loadInvites]);

  async function run(fn: () => Promise<unknown>, ok: string) {
    try {
      await fn();
      setMessage({ tone: 'ok', text: ok });
      await ws.engine.syncNow();
      await loadInvites();
    } catch (err) {
      setMessage({ tone: 'error', text: describeError(err) });
    }
  }

  return (
    <div className="mx-auto grid max-w-3xl gap-8 p-4 sm:p-8">
      <div>
        <h1 className="text-xl font-semibold">Account &amp; Team</h1>
        <p className="mt-1 text-slate">
          Who can use {ws.auth.orgName}, and the computers they use.
        </p>
      </div>
      {message ? (
        <div
          className={`rounded-md border px-3 py-2 text-[13px] ${message.tone === 'ok' ? 'border-green-200 bg-green-50 text-ok' : 'border-red-200 bg-red-50 text-flag'}`}
          role={message.tone === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </div>
      ) : null}

      <section className="grid gap-3">
        <h2 className="text-base font-semibold">You</h2>
        <div className="flex items-end gap-3">
          <Field label="Your name">
            <Input value={name} onChange={(e) => setName(e.target.value)} className="w-64" />
          </Field>
          <Button
            disabled={!name.trim() || name === ws.auth.userName}
            onClick={() =>
              run(
                () => api('PATCH', '/v1/me', { name: name.trim() }),
                'Name updated. It shows everywhere after your next sign-in.',
              )
            }
          >
            Save
          </Button>
        </div>
        <p className="text-[13px] text-slate">
          Signed in as {ws.auth.email ?? ws.auth.phone}. Roles:{' '}
          {ws.auth.roles.map((r) => ROLE_LABELS[r as Role] ?? r).join(', ')}.
        </p>
      </section>

      <PasswordPanel />

      <section className="grid gap-3">
        <h2 className="text-base font-semibold">Team</h2>
        <ul className="divide-y divide-line rounded-lg border border-line bg-paper">
          {(members?.rows ?? []).map((m) => (
            <MemberItem key={m.id} m={m} me={m.user_id === ws.auth.userId} run={run} />
          ))}
          {invites.map((i) => (
            <li key={i.id} className="flex items-center gap-3 px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="font-medium">{i.displayName || i.identifier}</div>
                <div className="text-[13px] text-slate">
                  {i.identifier} · {i.roles.map((r) => ROLE_LABELS[r as Role] ?? r).join(', ')}
                </div>
              </div>
              <Badge tone="brand">Invited</Badge>
              {canInvite ? (
                <button
                  type="button"
                  aria-label={`Cancel invitation for ${i.identifier}`}
                  className="p-1 text-slate hover:text-flag"
                  onClick={() =>
                    run(() => api('DELETE', `/v1/invites/${i.id}`), 'Invitation cancelled.')
                  }
                >
                  <Trash2 size={15} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
        {canInvite ? (
          <InviteForm run={run} />
        ) : (
          <p className="text-[13px] text-slate">Only lab admins and owners can add people.</p>
        )}
      </section>

      <section className="grid gap-3">
        <h2 className="text-base font-semibold">Computers</h2>
        <ul className="divide-y divide-line rounded-lg border border-line bg-paper">
          {(devices?.rows ?? []).map((d) => {
            const here = d.id === ws.auth.deviceId;
            return (
              <li key={d.id} className="flex items-center gap-3 px-4 py-2.5">
                <Monitor size={18} className="text-slate" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium">
                    {d.name} {here ? <Badge tone="ok">This computer</Badge> : null}
                    {d.revoked_at ? <Badge tone="flag">Removed</Badge> : null}
                  </div>
                  <div className="text-[13px] text-slate">
                    {d.platform === 'web'
                      ? 'Web browser'
                      : d.platform === 'android'
                        ? 'Android phone'
                        : d.platform}
                    {d.last_seen_at
                      ? ` · last seen ${new Date(d.last_seen_at).toLocaleDateString('en-IN')}`
                      : ''}
                  </div>
                </div>
                {ws.can('manage_site') && !here && !d.revoked_at ? (
                  <Button
                    size="sm"
                    tone="danger"
                    onClick={() =>
                      run(
                        () => api('POST', `/v1/devices/${d.id}/revoke`),
                        'Computer removed. It will be signed out and its saved data cleared.',
                      )
                    }
                  >
                    Remove
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>
      <OfflinePinPanel />
      <DesktopPanel />
      <DeleteLabPanel />
    </div>
  );
}

function MemberItem({
  m,
  me,
  run,
}: {
  m: MemberRow;
  me: boolean;
  run: (fn: () => Promise<unknown>, ok: string) => Promise<void>;
}) {
  const ws = useWorkspace();
  const [open, setOpen] = useState(false);
  const [roles, setRoles] = useState<string[]>(m.roles);
  const canManage = ws.can('invite_users') && !me;
  const grantable = grantableRoles(ws.auth.roles);
  return (
    <li className="px-4 py-2.5">
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium">
            {m.display_name || 'Unnamed'} {me ? <Badge tone="brand">You</Badge> : null}
          </div>
          <div className="text-[13px] text-slate">
            {m.roles.map((r) => ROLE_LABELS[r as Role] ?? r).join(', ')}
          </div>
        </div>
        {canManage ? (
          <>
            <Button size="sm" onClick={() => setOpen((o) => !o)}>
              Roles
            </Button>
            <Button
              size="sm"
              tone="danger"
              onClick={() =>
                run(
                  () => api('DELETE', `/v1/members/${m.id}`),
                  `${m.display_name || 'They'} removed from the lab.`,
                )
              }
            >
              Remove
            </Button>
          </>
        ) : null}
      </div>
      {open ? (
        <div className="mt-2 grid gap-2 rounded-md bg-canvas p-3">
          <RolePicker value={roles} onChange={setRoles} options={grantable} />
          <div>
            <Button
              size="sm"
              tone="primary"
              disabled={roles.length === 0}
              onClick={() =>
                run(() => api('PATCH', `/v1/members/${m.id}`, { roles }), 'Roles updated.')
              }
            >
              Save roles
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

function RolePicker({
  value,
  onChange,
  options,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  options: Role[];
}) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
      {options.map((r) => (
        <label key={r} className="inline-flex items-center gap-1.5 text-[13px]">
          <input
            type="checkbox"
            className="accent-brand"
            checked={value.includes(r)}
            onChange={(e) =>
              onChange(e.target.checked ? [...value, r] : value.filter((x) => x !== r))
            }
          />
          {ROLE_LABELS[r]}
        </label>
      ))}
    </div>
  );
}

function InviteForm({ run }: { run: (fn: () => Promise<unknown>, ok: string) => Promise<void> }) {
  const ws = useWorkspace();
  const options = grantableRoles(ws.auth.roles);
  const [identifier, setIdentifier] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roles, setRoles] = useState<string[]>(['technician']);
  const [tried, setTried] = useState(false);
  const [seen, setSeen] = useState<Record<string, boolean>>({});
  const t = identifier.trim();
  const idProblem = !t
    ? 'Enter their email address or mobile number'
    : t.includes('@')
      ? emailProblem(t)
      : phoneProblem(t, true);
  const nameBad = displayName.trim() ? nameProblem(displayName) : null;
  const shownId = tried || seen.id ? idProblem : null;
  const shownName = tried || seen.name ? nameBad : null;
  return (
    <form
      className="grid gap-3 rounded-lg border border-line bg-paper p-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (idProblem || nameBad) return setTried(true);
        await run(
          () => api('POST', '/v1/invites', { identifier, displayName, roles }),
          'Invitation sent. They join the first time they sign in with that address.',
        );
        setIdentifier('');
        setDisplayName('');
      }}
    >
      <h3 className="flex items-center gap-2 text-[13px] font-semibold">
        <UserPlus size={15} /> Add someone
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Email or mobile number" error={shownId}>
          <Input
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            onBlur={() => setSeen((x) => ({ ...x, id: true }))}
            placeholder="abc@lab.example or 98765 43210"
          />
        </Field>
        <Field label="Their name" error={shownName}>
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            onBlur={() => setSeen((x) => ({ ...x, name: true }))}
            placeholder="Zee"
          />
        </Field>
      </div>
      <fieldset className="grid gap-1">
        <legend className="mb-1 text-[12px] font-medium text-slate">What can they do?</legend>
        <RolePicker value={roles} onChange={setRoles} options={options} />
      </fieldset>
      <div>
        <Button tone="primary" type="submit" disabled={roles.length === 0}>
          Send invitation
        </Button>
      </div>
    </form>
  );
}
